import { ReactElement, useEffect, useRef, useState } from 'react';
import { Component as ChevronUpIcon } from 'icons/icon-ui--chevron-up.svg';
import { Component as ChevronDownIcon } from 'icons/icon-ui--chevron-down.svg';
import { Component as PlantCheckIcon } from 'icons/icon-ui--plant-check.svg';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Component as CheckmarkIcon } from 'icons/icon-ui--checkmark.svg';
import { useFlags } from '@flagsmith/flagsmith/react';
import Dropin from 'braintree-web-drop-in';
import { AddressErrors, State } from 'models/Address';
import { ErrorMessage } from 'config';
import { Checkbox } from '../shared/checkbox/Checkbox';
import { Button } from '../shared/button/Button';
import styles from './Payment.module.scss';
import {
  useActivateSubscription,
  useCurrentUserQueryOnLoggedOut,
} from '../../services/user/user.queries';
import { useAuth } from '../../contexts/AuthContext';
import {
  MAX_ADDRESS2_LENGTH,
  PaymentService,
  PaymentType,
  PaymentUpdateFunnel,
  Route,
} from '../../utils/config';
import { gtmTrackOrderSuccess } from '../../services/gtm/gtm';
import { impactTrackSignup } from '../../services/impact/impact';
import { heapTrackSuccessfullyAuthPayment } from '../../services/heap/heap';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import LoadingSpoon from '../shared/loading/SubscriptionFoodLoader/LoadingSpoon';
import { PaymentForm } from '../shared/payment-form/PaymentForm';
import { useUpdatePaymentMutation } from '../../services/payment/payment.queries';
import { PaymentMethod } from '../../models/payment.types';
import { BillingAddressForm } from '../shared/address-form/BillingAddressForm';
import { useAddress } from '../../contexts/AddressContext';
import { Address, AddressSignupDraft } from '../../models/address.types';
import {
  useCreateBillingAddress,
  useFetchAddressesForLoggedout,
} from '../../services/address/address.queries';
import { friendBuyPurchaseEvent } from '../../services/friendbuy/friendbuy';

const PAYMENT_PROVIDER_BY_TYPE = {
  [PaymentType.CREDIT_CARD]: PaymentService.CREDIT_CARD,
  [PaymentType.GOOGLE_PAY]: PaymentService.GOOGLE_PAY,
  [PaymentType.PAYPAL]: PaymentService.PAYPAL,
  [PaymentType.APPLE_PAY]: PaymentService.APPLE_PAY,
};

// GTM best practice: bound how long we wait for the ordersuccess tags to fire
// before showing the Continue button anyway, via both dataLayer's eventTimeout
// and our own fallback timer (eventTimeout only helps if the GTM container
// script loaded at all).
const GTM_ORDER_SUCCESS_EVENT_TIMEOUT_MS = window.gon.gtm_ordersuccess_timeout;

interface Props {
  handleClose: (closeBySave?: boolean) => void;
  couponCode?: string;
}

export function Payment({
  handleClose,
  couponCode = undefined,
}: Props): ReactElement {
  const navigate = useNavigate();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasComponentMountedRef = useRef(false);
  const hasRedirectedAfterOrderSuccessRef = useRef(false);
  const [dropinInstance, setDropinInstance] = useState(null);
  const [displayLoadingAnimation, setDisplayLoadingAnimation] = useState(false);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(null);

  const [isConfirmSubscriptionChecked, setIsConfirmSubscriptionChecked] = useState(false);
  const [isBillingAddressSameAsShippingAddressChecked,
    setIsBillingAddressSameAsShippingAddressChecked] = useState(true);

  const [isSubscribeButtonClicked, setIsSubscribeButtonClicked] = useState(false);

  const [showSubscriptionAgreement, setShowSubscriptionAgreement] = useState(true);
  const [showBillingAddressForm, setShowBillingAddressForm] = useState(false);
  const toggleShowSubscriptionAgreement = () => setShowSubscriptionAgreement((prev) => !prev);
  const toggleBillingAddressForm = () => setShowBillingAddressForm((prev) => !prev);

  const flags = useFlags(['order_confirmation_page', 'display_subscription_continue_button']);
  const isOrderConfirmationEnabled = flags.order_confirmation_page?.enabled;
  const isDisplaySubscriptionContinueButtonEnabled = flags.display_subscription_continue_button?.enabled;
  const activateSubscriptionMutation = useActivateSubscription();
  const currentUser = useCurrentUserQueryOnLoggedOut(true).data;

  const { cart } = useMenuWithCart();

  const updatePaymentMutation = useUpdatePaymentMutation();
  useEffect(() => {
    if (updatePaymentMutation.isError) {
      setPaymentErrorMessage(
        updatePaymentMutation?.error?.message || 'There was an issue with your payment information.',
      );
    }
  }, [updatePaymentMutation?.error?.message, updatePaymentMutation.isError]);

  const {
    addressSignupDraft,
    setAddressSignupDraft,
    googleAutoComplete,
    updateCityAndStateUsingZipcode,
  } = useAddress();

  const useCreateBillingAddressMutation = useCreateBillingAddress();
  const { data: addresses } = useFetchAddressesForLoggedout();

  const {
    address1,
    address2,
    zip,
    state,
    city,
  } = addressSignupDraft;

  // refs used to scroll to error
  const baseRef = useRef<HTMLDivElement | null>(null);
  const addressInputRef = useRef<HTMLDivElement | null>(null);
  const address2InputRef = useRef<HTMLDivElement | null>(null);
  const zipRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef<HTMLDivElement | null>(null);
  const cityRef = useRef<HTMLDivElement | null>(null);

  const refsArray = useRef([
    { base: baseRef },
    { address: addressInputRef },
    { address2: address2InputRef },
    { zip: zipRef },
    { state: stateRef },
    { city: cityRef },
  ]);

  const handleFieldsChange = (update: AddressSignupDraft) => {
    setAddressSignupDraft((prev: AddressSignupDraft) => ({
      ...prev,
      ...update,
    }));
  };

  const [errors, setErrors] = useState<AddressErrors>({} as AddressErrors);

  const getErrorMsg = (attr) => (errors[attr] ? errors[attr][0] : '');
  const isInvalid = (attr) => !!errors[attr];

  useEffect(() => {
    if (addresses && addresses.length > 0 && isBillingAddressSameAsShippingAddressChecked) {
      const shippingAddress = addresses[0];
      setAddressSignupDraft((prev) => ({
        ...prev,
        address1: shippingAddress?.address,
        address2: shippingAddress?.address2,
        state: shippingAddress?.state,
        city: shippingAddress?.city,
        zip: shippingAddress?.zip,
      }));
      setShowBillingAddressForm(false);
    }
  }, [addresses, isBillingAddressSameAsShippingAddressChecked]);

  useEffect(() => {
    (async () => {
      await updateCityAndStateUsingZipcode(zip);
    })();
  }, [setAddressSignupDraft, zip]);

  // google autocomplete logic
  useEffect(() => {
    googleAutoComplete(addressInputRef);
  }, [addressInputRef.current, showBillingAddressForm]);

  useEffect(() => {
    if (address2.length <= MAX_ADDRESS2_LENGTH - 1) {
      setErrors((lastErrors) => {
        const newErrors = { ...lastErrors };
        delete newErrors.address2;
        return newErrors;
      });
    } else {
      setErrors((lastErrors) => ({
        ...lastErrors,
        address2: [ErrorMessage.CHAR_LIMIT_ADDRESS2],
      }));
    }
  }, [address2]);

  useEffect(() => {
    if (Object.keys(errors).length === 0) return;

    // Looping over the refsArray to find the first one with an error
    refsArray.current.some((ref) => {
      const fieldName = Object.keys(ref)[0];
      if (errors[fieldName] && ref[fieldName].current) {
        ref[fieldName].current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
        return true;
      }
      return false;
    });
  }, [errors, refsArray]);

  useEffect(() => {
    // only scroll to bottom if the subscription agreement is re-opened after initial render
    if (!hasComponentMountedRef.current) {
      hasComponentMountedRef.current = true;
      return;
    }
    if (showSubscriptionAgreement) {
      const scrollArea = scrollAreaRef.current;
      if (!scrollArea) return;

      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  }, [showSubscriptionAgreement]);

  useEffect(() => {
    if (updatePaymentMutation.isError && dropinInstance) {
      const instance: Dropin = dropinInstance;
      instance.clearSelectedPaymentMethod();
    }
  }, [updatePaymentMutation.isError, dropinInstance]);

  const normalizePaymentMethodAddress = (paymentDetails) => {
    let billingAddress;
    switch (paymentDetails.type) {
      case PaymentType.GOOGLE_PAY: {
        const googlePayBillingAddress = paymentDetails
          ?.details?.rawPaymentData?.paymentMethodData?.info?.billingAddress;
        if (googlePayBillingAddress) {
          billingAddress = {
            address1: googlePayBillingAddress.address1 || '',
            address2: googlePayBillingAddress.address2 || '',
            state: googlePayBillingAddress.administrativeArea || '',
            city: googlePayBillingAddress.locality || '',
            zip: googlePayBillingAddress.postalCode || '',
          };
        }
        break;
      }
      case PaymentType.PAYPAL: {
        // PayPal does not expose billing address -- use shipping address instead
        const payPalBillingAddress = paymentDetails?.details?.shippingAddress;
        if (payPalBillingAddress) {
          billingAddress = {
            address1: payPalBillingAddress.line1 || '',
            address2: payPalBillingAddress.line2 || '',
            state: payPalBillingAddress.state || '',
            city: payPalBillingAddress.city || '',
            zip: payPalBillingAddress.postalCode || '',
          };
        }
        break;
      }
      case PaymentType.APPLE_PAY: {
        const applePayBillingAddress = paymentDetails
          ?.details?.rawPaymentData?.billingContact;
        if (applePayBillingAddress) {
          billingAddress = {
            address1: applePayBillingAddress.addressLines[0] || '',
            address2: applePayBillingAddress.addressLines[1] || '',
            state: applePayBillingAddress.administrativeArea || '',
            city: applePayBillingAddress.locality || '',
            zip: applePayBillingAddress.postalCode || '',
          };
        }
        break;
      }
      case PaymentType.CREDIT_CARD:
      default:
        break;
    }

    return billingAddress;
  };

  const handleSelectPaymentMethod = (paymentDetails) => {
    setSelectedPaymentMethod({
      nonce: paymentDetails.nonce,
      type: paymentDetails.type,
      details: {
        lastFour: paymentDetails.details.lastFour,
        cardType: paymentDetails.details.cardType,
        expirationMonth: paymentDetails.details.expirationMonth,
        expirationYear: paymentDetails.details.expirationYear,
      },
      providerName: PAYMENT_PROVIDER_BY_TYPE[paymentDetails.type],
    });

    const billingAddress = normalizePaymentMethodAddress(paymentDetails);

    if (billingAddress) {
      setAddressSignupDraft((prev) => ({
        ...prev,
        ...billingAddress,
      }));
      setIsBillingAddressSameAsShippingAddressChecked(false);
      setShowBillingAddressForm(true);
    } else {
      // if the selected payment provider does not provide a billing address,
      // default to the shipping address entered on the Shipping step
      setIsBillingAddressSameAsShippingAddressChecked(true);
      setShowBillingAddressForm(false);
    }
  };

  const createBillingAddress = async () => {
    const billingAddressError = 'There was an issue with your billing address information.';
    if (!isBillingAddressSameAsShippingAddressChecked) {
      const stateKey = state as keyof typeof State;
      const address: Address = {
        address1,
        address2,
        city,
        state: stateKey,
        zip,
        userId: currentUser?.id,
      };

      const response = await useCreateBillingAddressMutation.mutateAsync(address);
      if (response.status === 422) {
        const parsedBody = response.parsedBody as AddressErrors;

        if (address1 && parsedBody.address && Object.keys(parsedBody).length === 1) {
          parsedBody.base = [ErrorMessage.UNVERIFIED_ADDRESS];
          if (address2) parsedBody.address2 = parsedBody.address;
        } else if (address1) {
          delete parsedBody.address;
        }

        setErrors(parsedBody);
        throw new Error(billingAddressError);
      }

      if (!response.ok) {
        throw new Error(billingAddressError);
      }

      setErrors({} as AddressErrors);
    }
  };

  const redirectAfterPayment = () => {
    if (hasRedirectedAfterOrderSuccessRef.current) return;
    hasRedirectedAfterOrderSuccessRef.current = true;
    if (isOrderConfirmationEnabled) {
      // Hard navigation avoids any React Router transition flash (shop page showing briefly).
      // sessionStorage carries the fromPayment flag across the full page load.
      sessionStorage.setItem('fromPayment', 'true');
      window.location.href = Route.ORDER_CONFIRMATION;
    } else {
      navigate(`${Route.HUB}#welcome`);
      window.location.reload();
      handleClose(true);
    }
  };

  const subscribe = async () => {
    try {
      if (selectedPaymentMethod) {
        setIsSubscribeButtonClicked(true);
        setPaymentErrorMessage(null);
        await createBillingAddress();
        await updatePaymentMutation.mutateAsync(selectedPaymentMethod);
        await activateSubscriptionMutation.mutateAsync(couponCode);
        document.cookie = 'is_prospect_user=false';
        heapTrackSuccessfullyAuthPayment(PaymentUpdateFunnel.STANDARD_SIGNUP,
          PAYMENT_PROVIDER_BY_TYPE[selectedPaymentMethod.type]);
        impactTrackSignup(currentUser!, cart!);
        friendBuyPurchaseEvent(currentUser!, cart!);
        setDisplayLoadingAnimation(true);

        // Show spinner then either auto-redirect or show Continue button after GTM fires.
        // isOrderConfirmationEnabled: auto-redirect (on) or Continue button (off).
        const fallbackTimeoutId = window.setTimeout(() => {
          const errorMessage = 'GTM ordersuccess eventCallback did not fire before timeout';
          if (typeof Rollbar !== 'undefined') {
            Rollbar.error(errorMessage, {
              timeoutMs: GTM_ORDER_SUCCESS_EVENT_TIMEOUT_MS,
            });
          } else {
            console.error(errorMessage);
          }
          if (isDisplaySubscriptionContinueButtonEnabled) {
            setShowContinueButton(true);
          } else {
            redirectAfterPayment();
          }
        }, GTM_ORDER_SUCCESS_EVENT_TIMEOUT_MS);

        gtmTrackOrderSuccess(currentUser!, cart!, () => {
          window.clearTimeout(fallbackTimeoutId);
          if (isDisplaySubscriptionContinueButtonEnabled) {
            setShowContinueButton(true);
          } else {
            redirectAfterPayment();
          }
        }, GTM_ORDER_SUCCESS_EVENT_TIMEOUT_MS);
      }
    } catch (Error) {
      setIsSubscribeButtonClicked(false);
      setPaymentErrorMessage(Error?.message || 'Something went wrong while subscribing.');
    }
  };

  return (
    <AnimatePresence>
      {displayLoadingAnimation ? (
        <div className={styles.loading_fork_container}>
          <span>
            <CheckmarkIcon />
            {' '}
            <b>Subscription confirmed!</b>
          </span>
          <br />
          <span>This is the start of something good...</span>
          {showContinueButton ? (
            <Button
              className={styles.continue_button}
              text="Continue"
              onClick={redirectAfterPayment}
            />
          ) : (
            <LoadingSpoon />
          )}
        </div>
      ) : (
        <div className={styles.payment_step_container}>
          <div
            className={styles.payment_container}
            ref={scrollAreaRef}
          >
            <PaymentForm
              dropinInstance={dropinInstance}
              setDropinInstance={setDropinInstance}
              paymentErrorMessage={paymentErrorMessage}
              handleSelectPaymentMethod={handleSelectPaymentMethod}
            />
            <div
              className={styles.banner}
            >
              <PlantCheckIcon width={26} height={26} />
              Skip or cancel anytime; no commitment required.
            </div>
            <div className={styles.billing_address}>
              <div className={styles.checkbox}>
                <Checkbox
                  id="Billing Address Same as Shipping Address"
                  onChange={() => {
                    setIsBillingAddressSameAsShippingAddressChecked(
                      !isBillingAddressSameAsShippingAddressChecked,
                    );
                  }}
                  isChecked={isBillingAddressSameAsShippingAddressChecked}
                  color="#297B03"
                />

                <div
                  className={styles.accordion_button}
                  tabIndex={0}
                  role="button"
                  onClick={toggleBillingAddressForm}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleBillingAddressForm();
                    }
                  }}
                >
                  Billing Address Same as Shipping Address
                  {showBillingAddressForm
                    ? <ChevronUpIcon width="12px" className="c-svg-fill-primary-color" />
                    : <ChevronDownIcon width="12px" className="c-svg-fill-primary-color" />}
                </div>
              </div>
            </div>
            {showBillingAddressForm && (
              <div className={styles.address_container}>
                <BillingAddressForm
                  values={{
                    address1,
                    address2,
                    zip,
                    state,
                    city,
                  }}
                  onChange={handleFieldsChange}
                  getErrorMsg={getErrorMsg}
                  isInvalid={isInvalid}
                  refs={{
                    baseRef,
                    addressInputRef,
                    address2InputRef,
                    zipRef,
                    cityRef,
                    stateRef,
                  }}
                  className={styles.address_form}
                />
              </div>
            )}

            <div className={styles.confirm_subscription}>
              <div className={styles.checkbox}>
                <Checkbox
                  id="Confirm Subscription"
                  onChange={() => setIsConfirmSubscriptionChecked(!isConfirmSubscriptionChecked)}
                  isChecked={isConfirmSubscriptionChecked}
                  color="#297B03"
                />

                <div
                  className={styles.accordion_button}
                  tabIndex={0}
                  role="button"
                  onClick={toggleShowSubscriptionAgreement}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleShowSubscriptionAgreement();
                    }
                  }}
                >
                  Confirm Subscription
                  {showSubscriptionAgreement
                    ? <ChevronUpIcon width="12px" className="c-svg-fill-primary-color" />
                    : <ChevronDownIcon width="12px" className="c-svg-fill-primary-color" />}
                </div>
              </div>
              {showSubscriptionAgreement && (
                <div className={styles.confirm_subscription_text}>
                  <p>
                    When you check this box, you agree to automatically renew your
                    subscription each week. Based on your current cart selections, we've selected
                    our
                    {' '}
                    <em>
                      {cart?.plan.name}
                    </em>
                    {' '}
                    {' '}
                    plan
                    for you. You&apos;ll be charged that plan&apos;s price of
                    <span className="payment-method__subscription_agreement_plan_price">
                                          &nbsp;
                      {cart?.plan.price}
                      *&nbsp;
                    </span>
                    + any applicable shipping (less any coupons or discounts) every
                    Wednesday. (*The amount charged may change depending on your order.)
                    You can cancel your subscription at any time, you just need to do so
                    by 11:59PM EST on Tuesday
                    <span className="u-text--italic">
                                          &nbsp;the week before your next scheduled delivery&nbsp;
                    </span>
                    to avoid being charged for another box. You can cancel or change your
                    meal plan or payment method any time by logging into your account at
                    &nbsp;
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://www.purplecarrot.com/"
                    >
                      https://www.purplecarrot.com/
                    </a>
                    &nbsp;or by contacting us at&nbsp;
                    <a href="callto://857-703-8188">
                      857-703-8188
                    </a>
                    .
                  </p>
                  <p>
                    <a href="/terms?viewport=iframe">
                      Click here to review the Purple Carrot terms of service
                    </a>
                    .
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className={styles.payment_button_container}>
            <Button
              className={styles.payment_button}
              text="Subscribe"
              onClick={subscribe}
              isEnabled={isConfirmSubscriptionChecked
                && (isBillingAddressSameAsShippingAddressChecked
                  || (!isBillingAddressSameAsShippingAddressChecked
                    && address1 !== '' && state !== '' && zip !== '')
                )
                && !!selectedPaymentMethod
                && !isSubscribeButtonClicked
                && !cart?.priceSummary?.remainingMinimumAmount}
            />

            <p>Sign up to customize the recipes on your box each week</p>
          </div>
        </div>
      )}

    </AnimatePresence>
  );
}
